/**
 * A panel hung off a top-bar button. Chrome furniture, deliberately NOT a route.
 *
 * WHY NOT A ROUTE, because it is the first question to ask in this repo. `CLAUDE.md` rule 1:
 * navigation is a manifest built complete on day one, and later work "changes a `status` from
 * `dayN` to `live`. They never add a route." Notifications and settings are in no section of
 * `CONSOLES.md` §2 and in no screen list, so giving either one an address would mean adding a
 * route the manifest never declared -- the exact move the rule forbids, and `routes.check.ts`
 * would be right to be updated against it rather than the other way round. They are chrome:
 * they sit above every route, they have no address, and they render in place.
 */
import { useEffect, useId, useRef, type ReactNode } from 'react'

export interface PopoverProps {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  /** Accessible label for the trigger this panel belongs to. */
  labelledBy?: string
}

export function Popover({ open, onClose, title, children, labelledBy }: PopoverProps) {
  const ref = useRef<HTMLDivElement | null>(null)
  const headingId = useId()

  useEffect(() => {
    if (!open) return

    // Escape closes, and a click anywhere outside closes. Both, not one: a panel that only
    // closes on Escape traps a mouse user, and one that only closes on an outside click
    // traps a keyboard user behind a thing they cannot dismiss.
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose()
    }
    const onDown = (event: MouseEvent): void => {
      const node = ref.current
      if (!node) return
      const target = event.target as Node | null
      // The trigger handles its own toggle. Closing here as well would fire both and leave
      // the panel exactly as it was, which reads as a button that does nothing.
      if (target && (node.contains(target) || (target as Element).closest?.('[data-popover-trigger]'))) return
      onClose()
    }

    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onDown)
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="popover"
      role="dialog"
      aria-labelledby={labelledBy ?? headingId}
      ref={ref}
    >
      <div className="popover-head">
        <span id={headingId} className="popover-title">
          {title}
        </span>
        <button type="button" className="popover-close" onClick={onClose} aria-label={`Close ${title}`}>
          &times;
        </button>
      </div>
      <div className="popover-body">{children}</div>
    </div>
  )
}
